import React, { useEffect, useState } from "react";
import { DoorOpen, DoorClosed, MessageSquare, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";

export function AdminSiteStatus() {
  const [st, setSt] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/site-status").then((r) => { setSt(r.data); setMessage(r.data.message || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (new_accounts) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/site-status", { new_accounts, message: message.trim() || null });
      setSt(r.data); setMessage(r.data.message || "");
      toast.success(new_accounts === "blocked" ? "New accounts are now blocked" : "New accounts are now allowed");
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };

  const blocked = st?.new_accounts === "blocked";

  return (
    <div className="space-y-4" data-testid="admin-site-status">
      <div className={`ss-card p-5 border ${blocked ? "border-rose/60" : "border-champagne"}`} data-testid="site-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cocoa/50">New accounts</p>
            <p className="mt-1 font-heading text-2xl text-cocoa" data-testid="site-status-value">
              {!st ? "Loading…" : blocked ? "BLOCKED — invite-only" : "ALLOWED — anyone can sign up"}
            </p>
            {st?.updated_by && <p className="text-xs text-cocoa/55 mt-1">Last changed by {st.updated_by} · {new Date(st.updated_at).toLocaleString()}</p>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => save("blocked")} disabled={busy || blocked} data-testid="site-status-block" className={`ss-pill h-11 disabled:opacity-50 ${blocked ? "ss-pill-ghost" : "bg-rose text-ivory"}`}><DoorClosed size={14} /> Block new accounts</button>
            <button onClick={() => save("open")} disabled={busy || (st && !blocked)} data-testid="site-status-allow" className={`ss-pill h-11 disabled:opacity-50 ${blocked ? "ss-pill-primary" : "ss-pill-ghost"}`}><DoorOpen size={14} /> Allow new accounts</button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="ss-card p-5">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50 inline-flex items-center gap-1.5"><MessageSquare size={11} /> Message shown when someone tries to sign up</p>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} rows={3} data-testid="site-status-message-input"
            className="mt-2 w-full bg-ivory border border-champagne rounded-md p-3 text-sm outline-none focus:border-mauve" />
          <button onClick={() => save(st?.new_accounts || "open")} disabled={busy || !st} data-testid="site-status-message-save" className="mt-2 text-xs text-mauve underline disabled:opacity-50">Save message</button>
        </div>
        <div className="ss-card p-5 text-sm text-cocoa/75 leading-relaxed">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50 inline-flex items-center gap-1.5"><ShieldCheck size={11} /> What "blocked" does</p>
          <ul className="mt-2 space-y-1.5 list-disc pl-5">
            <li>The Create account form stays visible, but submitting it shows your message above.</li>
            <li><strong>Ambassador invite links</strong> still work and create the account as usual.</li>
            <li><strong>Accounts you create</strong> from Members → New account still activate through their set-password link.</li>
            <li>Partner-invite links are paused until you allow new accounts again. Existing members and logins are unaffected — this is separate from Maintenance mode.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
